import React from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Icons } from "./Machine360Icons";
import Nodata from "../common-component/Nodata";

ChartJS.register(ArcElement, Tooltip, Legend);

export const RunStatusChart = ({ data, isLoading = false }) => {
  if (isLoading) {
    return (
      <div className="m360-card m360-run-status-card">
        <div className="m360-card-header">
          <div className="m360-card-title-group">
            <Skeleton circle={true} height={16} width={16} />
            <Skeleton height={16} width={150} />
          </div>
        </div>
        <div className="m360-run-status-body">
          <div
            className="m360-donut-wrap d-flex align-items-center justify-content-center"
            style={{ minHeight: 120 }}
          >
            <Skeleton circle={true} height={110} width={110} />
          </div>
          <div className="m360-run-status-legend">
            {[0, 1, 2].map((i) => (
              <div key={i} className="m360-legend-row">
                <div className="m360-legend-left">
                  <Skeleton circle={true} height={9} width={9} />
                  <Skeleton height={13} width={75} />
                </div>
                <Skeleton height={13} width={55} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const title = data?.title || "Run Status (Last 30 Days)";
  const isAllZero =
    !data ||
    !Number(data.totalRuns) ||
    !data.breakdown ||
    data.breakdown.every((item) => Number(item.count) === 0);

  const chartData = {
    labels: (data?.breakdown || []).map((item) => item.label),
    datasets: [
      {
        data: (data?.breakdown || []).map((item) => item.count),
        backgroundColor: (data?.breakdown || []).map((item) => item.color),
        hoverBackgroundColor: (data?.breakdown || []).map((item) => item.color),
        borderWidth: 0,
        cutout: "75%",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(17, 17, 17, 0.92)",
        padding: 8,
        cornerRadius: 4,
        titleFont: { family: "'Sora', sans-serif", size: 11 },
        bodyFont: { family: "'Sora', sans-serif", size: 11 },
        callbacks: {
          label: (context) => {
            const item = data?.breakdown?.[context.dataIndex];
            return item ? ` ${item.label}: ${item.count} (${item.percentage})` : "";
          },
        },
      },
    },
  };

  return (
    <div className="m360-card m360-run-status-card">
      <div className="m360-card-header">
        <div className="m360-card-title-group">
          <Icons.Clock size={16} className="m360-card-header-icon" />
          <h4 className="m360-card-title">{title}</h4>
        </div>
      </div>

      {isAllZero ? (
        <div className="m360-card-nodata-wrapper">
          <Nodata />
        </div>
      ) : (
        <div className="m360-run-status-body">
          {/* Donut Container with Centered Text */}
          <div className="m360-donut-wrap">
            <div className="m360-donut-chart">
              <Doughnut data={chartData} options={chartOptions} />
            </div>
            <div className="m360-donut-center">
              <span className="m360-donut-center-value">{data?.totalRuns}</span>
              <span className="m360-donut-center-label">Total Runs</span>
            </div>
          </div>

          {/* Legend List */}
          <div className="m360-run-status-legend">
            {data?.breakdown?.map((item, idx) => (
              <div key={idx} className="m360-legend-row">
                <div className="m360-legend-left">
                  <span
                    className="m360-legend-dot"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="m360-legend-name">{item.label}</span>
                </div>
                <span className="m360-legend-metric">
                  <span className="m360-legend-count">{item.count}</span>{" "}
                  <span className="m360-legend-pct">({item.percentage})</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

